import React, { useState, useEffect, useRef, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;

  @media (max-width: 768px) {
    max-width: 280px;
  }
`;

const SearchInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 40px 10px 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  background: #f8fafc;
  color: #1e293b;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  color: #64748b;
  font-size: 1rem;
  pointer-events: none;
`;

const SearchClearButton = styled.button`
  position: absolute;
  right: 8px;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 50%;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #64748b;
  }
`;

const SearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  margin-top: 8px;
  z-index: 50;
  overflow: hidden;
  animation: ${fadeIn} 0.2s ease;
  max-height: 480px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }
`;

const SearchHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
`;

const SearchHeaderTitle = styled.h3`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
`;

const SearchFilters = styled.div`
  display: flex;
  gap: 8px;
`;

const FilterButton = styled.button`
  padding: 4px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: ${(props) => (props.active ? "#3b82f6" : "white")};
  color: ${(props) => (props.active ? "white" : "#64748b")};
  font-size: 0.7rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => (props.active ? "#3b82f6" : "#cbd5e1")};
    background: ${(props) => (props.active ? "#2563eb" : "#f8fafc")};
  }
`;

const SearchSection = styled.div`
  padding: 12px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }
`;

const SectionHeader = styled.div`
  padding: 8px 20px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionCount = styled.span`
  background: #e2e8f0;
  color: #64748b;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.65rem;
`;

const SearchResultItem = styled.div`
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;

  &:hover,
  &.highlighted {
    background: #f8fafc;
    border-left-color: #3b82f6;
  }

  &.highlighted {
    background: #eff6ff;
  }
`;

const ResultContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const ResultIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;

  &.people {
    background: #dbeafe;
    color: #1e40af;
  }

  &.okrs {
    background: #dcfce7;
    color: #166534;
  }

  &.assessments {
    background: #fef3c7;
    color: #92400e;
  }

  &.feedbacks {
    background: #f3e8ff;
    color: #7c3aed;
  }

  &.meetings {
    background: #e0f2fe;
    color: #0369a1;
  }

  &.pdis {
    background: #fee2e2;
    color: #991b1b;
  }
`;

const ResultInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ResultTitle = styled.div`
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
  line-height: 1.3;

  .highlight {
    background: #fef3c7;
    color: #92400e;
    padding: 1px 2px;
    border-radius: 2px;
  }
`;

const ResultDescription = styled.div`
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.4;

  .highlight {
    background: #fef3c7;
    color: #92400e;
    padding: 1px 2px;
    border-radius: 2px;
  }
`;

const ResultMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-size: 0.7rem;
  color: #94a3b8;
`;

const RecentSearches = styled.div`
  padding: 16px 20px;
`;

const RecentSearchesTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
`;

const RecentSearchItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: #64748b;
  font-size: 0.8rem;
  transition: all 0.2s ease;

  &:hover {
    color: #1e293b;
  }
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #94a3b8;
`;

const EmptyIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 8px;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 0.9rem;
`;

const SearchFooter = styled.div`
  padding: 12px 20px;
  background: #fafbfc;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: between;
  align-items: center;
  font-size: 0.7rem;
  color: #94a3b8;
`;

const KeyboardTip = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Key = styled.kbd`
  padding: 2px 4px;
  background: #e2e8f0;
  border: 1px solid #cbd5e1;
  border-radius: 2px;
  font-size: 0.65rem;
  font-family: monospace;
`;

const ViewAllButton = styled.button`
  padding: 4px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: white;
  color: #64748b;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const GlobalSearch = ({
  placeholder = "Pesquisar pessoas, OKRs, avaliações...",
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [activeFilter, setActiveFilter] = useState("all");
  const [recentSearches, setRecentSearches] = useState([]);

  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("search-recent");
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.length < 2) {
        setResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Mock API call - replace with actual search API
        await new Promise((resolve) => setTimeout(resolve, 300));

        const mockResults = generateMockResults(searchQuery, activeFilter);
        setResults(mockResults);
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [activeFilter],
  );

  useEffect(() => {
    if (query.trim()) {
      setLoading(true);
      debouncedSearch(query);
    } else {
      setResults([]);
      setLoading(false);
    }
  }, [query, debouncedSearch]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    const totalItems = results.length;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && results[highlightedIndex]) {
          handleResultClick(results[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleResultClick = (result) => {
    addToRecentSearches(query);
    navigate(result.path);
    setIsOpen(false);
    setQuery("");
    setHighlightedIndex(-1);
  };

  const handleRecentSearchClick = (searchQuery) => {
    setQuery(searchQuery);
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleClearSearch = () => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const addToRecentSearches = (searchQuery) => {
    if (!searchQuery.trim()) return;

    const updated = [
      searchQuery,
      ...recentSearches.filter((item) => item !== searchQuery),
    ].slice(0, 5);

    setRecentSearches(updated);
    localStorage.setItem("search-recent", JSON.stringify(updated));
  };

  const highlightText = (text, query) => {
    if (!query) return text;

    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="highlight">
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const getFilteredResults = () => {
    if (activeFilter === "all") return results;
    return results.filter(
      (result) => result.category.toLowerCase() === activeFilter,
    );
  };

  const groupedResults = groupResultsByCategory(getFilteredResults());
  const totalResults = getFilteredResults().length;

  return (
    <SearchContainer ref={searchRef}>
      <SearchInputContainer>
        <SearchIcon>🔍</SearchIcon>
        <SearchInput
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        {query && (
          <SearchClearButton onClick={handleClearSearch}>✕</SearchClearButton>
        )}
      </SearchInputContainer>

      {isOpen && (
        <SearchResults>
          {query.length >= 2 ? (
            <>
              <SearchHeader>
                <SearchHeaderTitle>
                  {loading
                    ? "Buscando..."
                    : `${totalResults} resultados para "${query}"`}
                </SearchHeaderTitle>
                <SearchFilters>
                  <FilterButton
                    active={activeFilter === "all"}
                    onClick={() => setActiveFilter("all")}
                  >
                    Todos
                  </FilterButton>
                  <FilterButton
                    active={activeFilter === "people"}
                    onClick={() => setActiveFilter("people")}
                  >
                    Pessoas
                  </FilterButton>
                  <FilterButton
                    active={activeFilter === "okrs"}
                    onClick={() => setActiveFilter("okrs")}
                  >
                    OKRs
                  </FilterButton>
                  <FilterButton
                    active={activeFilter === "assessments"}
                    onClick={() => setActiveFilter("assessments")}
                  >
                    Avaliações
                  </FilterButton>
                </SearchFilters>
              </SearchHeader>

              {loading ? (
                <EmptyState>
                  <EmptyIcon>⏳</EmptyIcon>
                  <EmptyText>Buscando resultados...</EmptyText>
                </EmptyState>
              ) : totalResults > 0 ? (
                <>
                  {Object.entries(groupedResults).map(([category, items]) => (
                    <SearchSection key={category}>
                      <SectionHeader>
                        {getCategoryName(category)}
                        <SectionCount>{items.length}</SectionCount>
                      </SectionHeader>
                      {items.map((result) => {
                        const flatIndex =
                          getFlattened(groupedResults).indexOf(result);
                        return (
                          <SearchResultItem
                            key={result.id}
                            className={
                              flatIndex === highlightedIndex
                                ? "highlighted"
                                : ""
                            }
                            onClick={() => handleResultClick(result)}
                          >
                            <ResultContent>
                              <ResultIcon
                                className={result.category.toLowerCase()}
                              >
                                {getResultIcon(result.category)}
                              </ResultIcon>
                              <ResultInfo>
                                <ResultTitle>
                                  {highlightText(result.title, query)}
                                </ResultTitle>
                                <ResultDescription>
                                  {highlightText(result.description, query)}
                                </ResultDescription>
                                <ResultMeta>
                                  <span>{result.meta}</span>
                                  {result.status && (
                                    <span>• {result.status}</span>
                                  )}
                                </ResultMeta>
                              </ResultInfo>
                            </ResultContent>
                          </SearchResultItem>
                        );
                      })}
                    </SearchSection>
                  ))}

                  <SearchFooter>
                    <KeyboardTip>
                      <Key>↑</Key>
                      <Key>↓</Key> para navegar • <Key>Enter</Key> para
                      selecionar • <Key>Esc</Key> para fechar
                    </KeyboardTip>
                    <ViewAllButton
                      onClick={() =>
                        navigate("/search", {
                          state: { query, filter: activeFilter },
                        })
                      }
                    >
                      Ver todos os resultados
                    </ViewAllButton>
                  </SearchFooter>
                </>
              ) : (
                <EmptyState>
                  <EmptyIcon>🔍</EmptyIcon>
                  <EmptyText>
                    Nenhum resultado encontrado para "{query}"
                  </EmptyText>
                </EmptyState>
              )}
            </>
          ) : (
            recentSearches.length > 0 && (
              <RecentSearches>
                <RecentSearchesTitle>Buscas recentes</RecentSearchesTitle>
                {recentSearches.map((search, index) => (
                  <RecentSearchItem
                    key={index}
                    onClick={() => handleRecentSearchClick(search)}
                  >
                    🕐 {search}
                  </RecentSearchItem>
                ))}
              </RecentSearches>
            )
          )}
        </SearchResults>
      )}
    </SearchContainer>
  );
};

// Helper functions
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function generateMockResults(query, filter) {
  const allResults = [
    // People
    {
      id: "p1",
      category: "people",
      title: "Maria Santos",
      description: "Desenvolvedora Senior • Equipe Frontend",
      meta: "Tech Lead",
      status: "Ativa",
      path: "/people/maria-santos",
    },
    {
      id: "p2",
      category: "people",
      title: "João Silva",
      description: "Product Manager • Equipe de Produto",
      meta: "Manager",
      status: "Ativo",
      path: "/people/joao-silva",
    },
    // OKRs
    {
      id: "o1",
      category: "okrs",
      title: "Aumentar satisfação do cliente",
      description: "Melhorar NPS de 7.5 para 8.5 até final do trimestre",
      meta: "Q2 2024",
      status: "75% concluído",
      path: "/okrs/customer-satisfaction",
    },
    {
      id: "o2",
      category: "okrs",
      title: "Reduzir tempo de resposta da API",
      description: "Otimizar performance para menos de 200ms",
      meta: "Q2 2024",
      status: "90% concluído",
      path: "/okrs/api-performance",
    },
    // Assessments
    {
      id: "a1",
      category: "assessments",
      title: "Avaliação Trimestral Q2 2024",
      description: "Avaliação de performance e competências",
      meta: "Autoavaliação",
      status: "Pendente",
      path: "/assessments/q2-2024",
    },
    {
      id: "a2",
      category: "assessments",
      title: "Avaliação 360° Leadership",
      description: "Feedback de liderança e gestão de equipes",
      meta: "360°",
      status: "Em andamento",
      path: "/assessments/360-leadership",
    },
    // Feedbacks
    {
      id: "f1",
      category: "feedbacks",
      title: "Feedback sobre projeto Dashboard",
      description: "Excelente trabalho na implementação da nova interface",
      meta: "Ana Clara",
      status: "Positivo",
      path: "/feedbacks/dashboard-project",
    },
    // Meetings
    {
      id: "m1",
      category: "meetings",
      title: "Reunião 1:1 com Maria Santos",
      description: "Review de performance e definição de metas",
      meta: "Amanhã 14:00",
      status: "Agendada",
      path: "/meetings/1on1-maria",
    },
    // PDIs
    {
      id: "pd1",
      category: "pdis",
      title: "Certificação AWS Solutions Architect",
      description: "Meta de certificação para Q3 2024",
      meta: "Desenvolvimento Técnico",
      status: "75% concluído",
      path: "/pdis/aws-certification",
    },
  ];

  const filtered = allResults.filter((item) => {
    const matchesQuery =
      item.title.toLowerCase().includes(query.toLowerCase()) ||
      item.description.toLowerCase().includes(query.toLowerCase());

    if (filter === "all") return matchesQuery;
    return matchesQuery && item.category === filter;
  });

  return filtered;
}

function groupResultsByCategory(results) {
  return results.reduce((groups, result) => {
    const category = result.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(result);
    return groups;
  }, {});
}

function getFlattened(groupedResults) {
  return Object.values(groupedResults).flat();
}

function getCategoryName(category) {
  const names = {
    people: "Pessoas",
    okrs: "OKRs",
    assessments: "Avaliações",
    feedbacks: "Feedbacks",
    meetings: "Reuniões",
    pdis: "PDIs",
  };
  return names[category] || category;
}

function getResultIcon(category) {
  const icons = {
    people: "👤",
    okrs: "🎯",
    assessments: "📋",
    feedbacks: "💬",
    meetings: "👥",
    pdis: "📈",
  };
  return icons[category] || "📄";
}

export default GlobalSearch;
